import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Heart, MessageCircle, Users, Clock, MapPin, Award, Coffee, Shirt, Droplet, Smile
} from 'lucide-react';
import { mockVolunteers, chatSeeds } from '../data/mockData';
import DetailImageCarousel from '../components/DetailImageCarousel';
import ChatDrawer from '../components/ChatDrawer';
import MorphButton from '../components/MorphButton';
import { requireAuth } from '../components/AuthGate';
import { signUpForOpportunity, cancelSignup, hasSignedUp, getSignupCount } from '../lib/volunteer';
import { enrolInCourse, cancelEnrolment, hasEnrolled, getEnrolmentCount } from '../lib/learn';

const WHAT_TO_BRING_ICONS = { 'Comfortable attire': Shirt, 'Water bottle': Droplet, 'Positive attitude': Smile };
const ADDITIONAL_INFO_ICONS = { 'Certificate of Appreciation': Award, 'Refreshments provided': Coffee };

const AUTO_REPLIES = [
  "Thanks for sharing! We'll factor that into your assignment.",
  "Noted — our team will follow up shortly with more details.",
  "Great question! Let us check and get back to you soon.",
  "Appreciate you letting us know. We'll make sure it's arranged."
];

// Declared at module scope on purpose: defining it inside the page component
// would give React a new component type on every render, remounting the
// MorphButton and discarding the timer that fires its onComplete.
function ActionBar({
  className = '', isSaved, onToggleSave, messageCount, onOpenChat,
  joinButtonLabel, onJoin, onJoinComplete, joined, onCancel
}) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <button
        type="button"
        data-testid="detail-save-button"
        onClick={onToggleSave}
        aria-label="Save"
        className="w-11 h-11 rounded-full border border-border flex items-center justify-center flex-shrink-0"
      >
        <Heart className={`h-5 w-5 ${isSaved ? 'fill-rose-500 text-rose-500' : 'text-[#0F172A]'}`} />
      </button>
      <button
        type="button"
        data-testid="detail-chat-button"
        onClick={onOpenChat}
        className="h-11 px-4 rounded-full border border-border flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] flex-shrink-0"
      >
        <MessageCircle className="h-4.5 w-4.5" />
        Chat{messageCount ? ` (${messageCount})` : ''}
      </button>
      {joined && onCancel ? (
        // Already signed up (real record) -> offer to cancel instead of re-joining.
        <button
          type="button"
          data-testid="cancel-registration-button"
          onClick={onCancel}
          className="flex-1 h-11 rounded-full font-bold text-sm border border-primary text-primary bg-white hover:bg-primary/5"
        >
          Cancel Registration
        </button>
      ) : (
        <MorphButton
          data-testid="join-opportunity-button"
          idleLabel={joinButtonLabel}
          successLabel="You're In!"
          onClick={onJoin}
          onComplete={onJoinComplete}
          forceSuccess={joined}
          className="flex-1 h-11 rounded-full font-bold text-sm bg-primary text-white hover:bg-primary-hover"
        />
      )}
    </div>
  );
}

export default function VolunteerDetail({
  opportunity,
  savedIds,
  onToggleSave,
  onBack,
  participantLabel = 'Volunteers',
  joinButtonLabel = 'Join This Opportunity',
  initialChatEntry = null,
  onBackToInbox,
  onJoinComplete,
  kind = 'generic'
}) {
  const isVolunteer = kind === 'volunteer';
  const isLearn = kind === 'learn';
  const [joined, setJoined] = useState(false);
  // Live signup / enrolment count from our backend; falls back to the
  // catalogue's number until it loads.
  const [liveCount, setLiveCount] = useState(null);
  const [messages, setMessages] = useState(() => chatSeeds[opportunity.id] || []);
  const [draft, setDraft] = useState('');
  const [chatOpen, setChatOpen] = useState(initialChatEntry === 'inbox');
  const [chatEntryFrom, setChatEntryFrom] = useState(initialChatEntry === 'inbox' ? 'inbox' : 'card');

  const isSaved = savedIds.has(opportunity.id);
  const images = opportunity.images?.length ? opportunity.images : [opportunity.heroImage || opportunity.image];

  // For a real volunteer opportunity OR Learn course, reflect the member's
  // actual state on open: already signed up / enrolled, and how many others.
  useEffect(() => {
    if (!isVolunteer && !isLearn) return;
    let live = true;
    (async () => {
      const [already, count] = isLearn
        ? await Promise.all([hasEnrolled(opportunity.id), getEnrolmentCount(opportunity.id)])
        : await Promise.all([hasSignedUp(opportunity.id), getSignupCount(opportunity.id)]);
      if (!live) return;
      if (already) setJoined(true);
      if (count != null) setLiveCount(count);
    })();
    return () => { live = false; };
  }, [isVolunteer, isLearn, opportunity.id]);

  // Browsing is open; committing is where we ask who you are. Returning false
  // keeps the button idle so a dismissed gate leaves no false success state.
  const handleJoin = async () => {
    const authed = await requireAuth();
    if (!authed) return false;
    if (isVolunteer) {
      const { ok, already, error } = await signUpForOpportunity(opportunity.id, opportunity.title);
      if (!ok) {
        toast.error(error || 'Could not sign you up. Please try again.', { position: 'top-center' });
        return false;
      }
      setJoined(true);
      if (!already) setLiveCount((c) => (c == null ? c : c + 1));
      return true;
    }
    if (isLearn) {
      const { ok, already, error } = await enrolInCourse(opportunity.id, opportunity.title);
      if (!ok) {
        toast.error(error || 'Could not enrol you. Please try again.', { position: 'top-center' });
        return false;
      }
      setJoined(true);
      if (!already) setLiveCount((c) => (c == null ? c : c + 1));
      return true;
    }
    setJoined(true);
    return true;
  };

  const handleCancel = async () => {
    const { ok } = isLearn
      ? await cancelEnrolment(opportunity.id)
      : await cancelSignup(opportunity.id);
    if (!ok) {
      toast.error('Could not cancel. Please try again.', { position: 'top-center' });
      return;
    }
    setJoined(false);
    setLiveCount((c) => (c == null ? c : Math.max(0, c - 1)));
    toast.success(isLearn ? 'Enrolment cancelled.' : 'Registration cancelled.', { position: 'top-center' });
  };

  const handleSend = () => {
    if (!draft.trim()) return;
    const userMsg = { id: Date.now(), sender: 'me', text: draft.trim(), time: 'Now' };
    setMessages((prev) => [...prev, userMsg]);
    setDraft('');
    setTimeout(() => {
      const reply = AUTO_REPLIES[Math.floor(Math.random() * AUTO_REPLIES.length)];
      setMessages((prev) => [...prev, { id: Date.now() + 1, sender: 'org', text: reply, time: 'Now' }]);
    }, 1200);
  };

  const openChatFromCard = () => {
    setChatEntryFrom('card');
    setChatOpen(true);
  };

  const handleChatBack = () => {
    setChatOpen(false);
    if (chatEntryFrom === 'inbox') {
      onBackToInbox?.();
    }
  };

  // Shared heart / chat / CTA controls — rendered as a fixed footer bar on
  // mobile, and as an inline block near the top of the right column on desktop.
  const actionProps = {
    isSaved,
    onToggleSave: () => onToggleSave(opportunity.id),
    messageCount: messages.length,
    onOpenChat: openChatFromCard,
    joinButtonLabel,
    onJoin: handleJoin,
    onJoinComplete,
    joined,
    onCancel: (isVolunteer || isLearn) ? handleCancel : undefined
  };

  return (
    <div className="min-h-screen bg-background page-fade-in flex flex-col md:block" data-testid="volunteer-detail-page">
      <div className="md:desktop-shell md:py-8 md:flex md:flex-row md:gap-10 md:items-start">
        {/* Image carousel — sticky on the left on desktop, full-width top banner on mobile */}
        <div className="md:w-[46%] md:flex-shrink-0 md:sticky md:top-6 md:h-[560px]">
          <DetailImageCarousel
            images={images}
            extra={opportunity.carouselExtra}
            tag={opportunity.tag}
            tagColor={opportunity.tagColor}
            onBack={onBack}
          />
        </div>

        {/* Linear, scrollable body — Details + participants together */}
        <div className="flex-1 px-4 md:px-0 pt-4 md:pt-0 pb-28 md:pb-10" data-testid="detail-scroll-content">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#0F172A] leading-tight">{opportunity.title}</h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">{opportunity.location} &nbsp;·&nbsp; {opportunity.date}</p>

          {/* Desktop-only inline action row, placed right under the title (mobile uses the fixed footer below) */}
          <ActionBar className="hidden md:flex mt-5 mb-2" {...actionProps} />

          <div className="flex items-center gap-4 text-xs text-muted-foreground my-4">
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{opportunity.time}</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{opportunity.location}</span>
          </div>
          <p className="text-sm text-foreground leading-relaxed mb-4">{opportunity.description}</p>

          <div className="grid grid-cols-2 gap-3 mb-5 md:max-w-md">
            <div className="rounded-xl bg-muted/60 p-3">
              <Users className="h-4 w-4 text-primary mb-1" />
              <p className="text-xs font-bold text-[#0F172A]">{isLearn ? 'Learners Enrolled' : isVolunteer ? 'Volunteers Joined' : 'Volunteers Needed'}</p>
              <p className="text-sm font-extrabold text-primary">{(liveCount != null ? liveCount : opportunity.volunteersFilled) ?? 0}{opportunity.volunteersNeeded ? ` / ${opportunity.volunteersNeeded}` : ''}</p>
            </div>
            <div className="rounded-xl bg-muted/60 p-3">
              <Users className="h-4 w-4 text-primary mb-1" />
              <p className="text-xs font-bold text-[#0F172A]">Age Requirement</p>
              <p className="text-sm font-extrabold text-primary">{opportunity.ageRequirement}</p>
            </div>
          </div>

          <h3 className="text-sm font-bold text-[#0F172A] mb-2">What to Bring</h3>
          <div className="flex gap-4 mb-5 md:max-w-md">
            {opportunity.whatToBring.map((item) => {
              const Icon = WHAT_TO_BRING_ICONS[item] || Smile;
              return (
                <div key={item} className="flex flex-col items-center gap-1.5 flex-1 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="text-[11px] text-muted-foreground leading-tight">{item}</span>
                </div>
              );
            })}
          </div>

          <h3 className="text-sm font-bold text-[#0F172A] mb-2">Additional Info</h3>
          <div className="flex flex-col gap-2 mb-2">
            {opportunity.additionalInfo.map((item) => {
              const Icon = ADDITIONAL_INFO_ICONS[item] || Award;
              return (
                <div key={item} className="flex items-center gap-3 text-sm text-foreground">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4" />
                  </div>
                  {item}
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-5 border-t border-border/60" data-testid="detail-participants-section">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">
              {participantLabel} joining ({mockVolunteers.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {mockVolunteers.map((v) => (
                <div key={v.id} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {v.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{v.name}</p>
                    <p className="text-xs text-muted-foreground">{v.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only fixed action bar — replaces the global 4-tab nav while a card is open */}
      <ActionBar
        className="md:hidden fixed bottom-0 inset-x-0 z-[55] bg-white border-t border-border/60 px-3 py-2.5"
        {...actionProps}
      />

      <ChatDrawer
        isOpen={chatOpen}
        title={opportunity.title}
        subtitle="IhsanOne Volunteer Team"
        messages={messages}
        draft={draft}
        onDraftChange={setDraft}
        onSend={handleSend}
        onBack={handleChatBack}
      />
    </div>
  );
}
